import React, { useCallback, useEffect, useState } from 'react';
import * as actions from '../../actions/index';
import { useHistory, useParams } from 'react-router-dom';
import claimApplicationProjections from '../../projections/claimApplication';
import ExistingClaim from '../../components/existingClaim/existingClaim';
import questionProjections from '../../projections/question';
//@ts-ignore
import _ from 'lodash';
import questionFunctions from '../../utils/functions/question';
import GENERAL_FUNCTIONS from '../../utils/functions/functions';
import { useAppSelector, useAppDispatch } from '../../hooks/hooks';
import codes from '../../utils/constants/codes';
import currencyProjections from '../../projections/currency';

const ExistingClaimContainer = (): React.ReactElement => {
    const dispatch = useAppDispatch();
    let { id, templateId }: { id: string; templateId: string } = useParams();
    const history = useHistory();
    const currencyOptions: actions.CurrencySchema[] | null = useAppSelector(
        currencyProjections.currencies,
    );
    const claimApplication = useAppSelector(
        claimApplicationProjections.claimApplication(id),
    );
    const fetchingClaim = useAppSelector(
        state => state.claimApplication.loading,
    );
    const questions = useAppSelector(state => state.question.question.byId);
    const questionTypes = useAppSelector(
        state => state.questionType.questionType.byId,
    );
    const withDrawQuestions = useAppSelector(
        questionProjections.filterQuestionsByGroupCode(
            codes.updateWithdrawnDetailsGroupCode,
        ),
    );
    const submittingWithdrawnClaim = useAppSelector(
        state => state.claimApplication.submitting,
    );
    const [isWithdrawnModalOpen, setWithdrawnModalOpen] =
        useState<boolean>(false);
    const [openDocumentModal, setOpenDocumentModal] = useState<boolean>(false);
    const claimApplicationTemplate = useAppSelector(
        claimApplicationProjections.claimApplicationTemplate(id),
    );

    const fetchClaimApplicationAction = useCallback(
        (payload: actions.fetchClaimApplicationByIdAction | string) =>
            dispatch(actions.fetchClaimApplicationById(payload)),
        [dispatch],
    );

    const fetchQuestionsByGroupAction = useCallback(
        (payload: any) =>
            dispatch(actions.fetchQuestionsByGroup(payload as any)),
        [dispatch],
    );

    const fetchQuestionTypeAction = useCallback(
        () => dispatch(actions.fetchQuestionTypes()),
        [dispatch],
    );

    const fetchQuestionValidationRuleAction = useCallback(
        () => dispatch(actions.fetchQuestionValidationRule()),
        [dispatch],
    );

    const createWithdrawnClaimApplicationAction = useCallback(
        (payload: actions.createWithdrawnClaimApplicationAction) =>
            dispatch(actions.createWithdrawnClaimApplication(payload)),
        [dispatch],
    );

    const fetchCurrenciesAction = useCallback(
        () => dispatch(actions.fetchCurrencies()),
        [dispatch],
    );

    const fetchClaimApplicationTemplateAction = useCallback(
        (payload: actions.fetchClaimApplicationTemplateAction) =>
            dispatch(actions.fetchClaimApplicationTemplate(payload)),
        [dispatch],
    );

    useEffect(() => {
        fetchQuestionTypeAction();
    }, []);

    // fetch claim application on mount
    useEffect(() => {
        fetchClaimApplicationAction(id);
    }, []);

    useEffect(() => {
        if (currencyOptions === null) {
            fetchCurrenciesAction();
        }
    }, [currencyOptions, fetchCurrenciesAction]);

    useEffect(() => {
        fetchQuestionValidationRuleAction();
    }, [fetchQuestionValidationRuleAction]);

    useEffect(() => {
        fetchQuestionsByGroupAction({
            claimApplicationId: id,
            groupCode: codes.updateWithdrawnDetailsGroupCode,
        });
    }, [fetchQuestionsByGroupAction]);

    const openWithdrawModal = () => {
        setWithdrawnModalOpen(true);
    };

    const closeWithdrawModal = () => {
        if (claimApplication?.withdrawn) {
            history.push('/claims/list');
        } else {
            setWithdrawnModalOpen(false);
        }
    };

    useEffect(() => {
        if (templateId) {
            fetchClaimApplicationTemplateAction({
                templateId: templateId,
                claimApplicationId: id,
            });
            setOpenDocumentModal(true);
        }
    }, [templateId]);

    const onCloseDocumentModal = () => {
        setOpenDocumentModal(false);
        history.replace(`/claims/${id}?tab=communication`);
    };

    const onSubmitWithDrawnClaimApplication = (formAnswers: any) => {
        let filteredQuestions = GENERAL_FUNCTIONS.filterObjectBy(
            questions,
            (q: { groupCode: string }) =>
                q.groupCode === codes.updateWithdrawnDetailsGroupCode,
        );
        const answers = questionFunctions.constructAnswersDTO(
            filteredQuestions,
            questionTypes,
            _.pickBy(
                formAnswers,
                (val: any, key: string | number) => !!filteredQuestions[key],
            ),
            null,
            [],
        );

        createWithdrawnClaimApplicationAction({
            data: { Answers: answers },
            claimApplicationId: claimApplication?.id,
        });
    };

    return (
        <ExistingClaim
            claimApplication={claimApplication}
            fetchingClaim={fetchingClaim}
            openWithdrawModal={openWithdrawModal}
            closeWithdrawModal={closeWithdrawModal}
            isWithdrawnModalOpen={isWithdrawnModalOpen}
            withDrawQuestions={withDrawQuestions}
            submittingWithdrawnClaim={submittingWithdrawnClaim}
            questionTypes={questionTypes}
            onSubmitWithDrawnClaimApplication={
                onSubmitWithDrawnClaimApplication
            }
            openDocumentModal={openDocumentModal}
            onCloseDocumentModal={onCloseDocumentModal}
            claimApplicationTemplate={claimApplicationTemplate}
        />
    );
};

export default ExistingClaimContainer;
